import React, { useState, useEffect, useRef, useCallback } from "react";
import { Icon } from "@iconify/react";
import Select from "react-select";
import { toast } from "react-toastify";
import { getData } from "../../../webService/webservice.js";
import { useStore } from "../../../Context/context.jsx";
import { getDateRange } from "../../../utils/get-DateRange.js";
import "../../../styles/Auth/expense-report.css";

const formatDate = (d) => {
    if (!d) return "—";
    return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const EXPENSE_NAMES = [
    "Rent", "Utilities", "Salary", "Supplies", "Marketing",
    "Maintenance", "Travel", "Food", "Equipment", "Other"
];

const PAYMENT_MODES = ["CASH", "CARD", "UPI", "OTHERS"];

const COLUMN_LABELS = {
    date: "Date",
    expenseName: "Expense Category",
    description: "Description",
    amount: "Amount",
    expenseType: "Type",
    paymentMode: "Payment Mode",
    invoiceNo: "Invoice No.",
    count: "Total Records",
    action: "Action"
};

const ALL_COLUMNS = ["date", "expenseName", "description", "amount", "expenseType", "paymentMode", "invoiceNo", "count", "action"];

const TAB_COLUMNS = {
    "overall-expenses": ["date", "expenseName", "description", "amount", "expenseType", "paymentMode", "invoiceNo", "action"],
    "category-wise": ["expenseName", "count", "amount", "action"]
};

export default function ExpenseReport() {
    const today = new Date().toISOString().split("T")[0];
    const { selectedStoreId } = useStore();
    const sessionToken = localStorage.getItem("Token");
    const role = localStorage.getItem("role");
    const isSuperAdmin = role === "SuperAdmin" || role === "SelfAdmin";

    // ── Tabs ──
    const [activeTab, setActiveTab] = useState("overall-expenses");

    // ── Date ──
    const [startDate, setStartDate] = useState(today);
    const [endDate, setEndDate] = useState(today);
    const [dateRange, setDateRange] = useState("Today");

    // ── Data ──
    const [data, setData] = useState([]);
    const [summary, setSummary] = useState({ totalBillableAmount: 0, totalNonBillableAmount: 0, overallTotalAmount: 0 });
    const [loading, setLoading] = useState(false);

    // ── Pagination ──
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    // ── Filters ──
    const [filterExpenseName, setFilterExpenseName] = useState("");
    const [filterInvoiceNo, setFilterInvoiceNo] = useState("");
    const [filterStoreId, setFilterStoreId] = useState("");
    const [filterMinAmount, setFilterMinAmount] = useState("");
    const [filterMaxAmount, setFilterMaxAmount] = useState("");
    const [storeList, setStoreList] = useState([]);
    const [appliedFilters, setAppliedFilters] = useState({});

    // ── Table ──
    const [tableSearch, setTableSearch] = useState("");
    const [showColumnToggler, setShowColumnToggler] = useState(false);
    const [visibleColumns, setVisibleColumns] = useState(
        Object.fromEntries(ALL_COLUMNS.map(c => [c, true]))
    );
    const columnTogglerRef = useRef(null);

    // ── Modal ──
    const [selectedRecord, setSelectedRecord] = useState(null);

    // ── Fetch Stores ──
    useEffect(() => {
        const fetchStores = async () => {
            try {
                const res = await getData("stores/stores", { limit: 1000 }, sessionToken);
                if (res?.data) setStoreList(Array.isArray(res.data) ? res.data : (res.data.results || []));
            } catch (err) { console.error(err); }
        };
        fetchStores();
    }, [sessionToken]);

    // ── Click outside column toggler ──
    useEffect(() => {
        const handler = (e) => {
            if (columnTogglerRef.current && !columnTogglerRef.current.contains(e.target))
                setShowColumnToggler(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    // ── Date Range Picker ──
    const handleDateRangeChange = (range) => {
        setDateRange(range);
        if (range === "Custom") return;
        const { startDate: s, endDate: e } = getDateRange(range);
        setStartDate(s);
        setEndDate(e);
    };

    // ── Fetch ──
    const fetchReport = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                startDate,
                endDate,
                page: currentPage,
                limit: rowsPerPage,
                tab: activeTab,
                ...appliedFilters,
                storeId: appliedFilters.storeId || selectedStoreId || ""
            };

            const res = await getData("reports/reports/expense-reports", params, sessionToken);

            if (res?.status === 200 || res?.success) {
                setData(res.data?.data || []);
                setSummary(res.data?.summary || { totalBillableAmount: 0, totalNonBillableAmount: 0, overallTotalAmount: 0 });
                const pg = res.data?.pagination || {};
                setTotalItems(pg.totalItems || 0);
                setTotalPages(pg.totalPages || 1);
            } else {
                toast.error(res?.message || "Failed to fetch expense report");
            }
        } catch (err) {
            console.error(err);
            toast.error("Something went wrong");
        } finally {
            setLoading(false);
        }
    }, [startDate, endDate, currentPage, rowsPerPage, selectedStoreId, appliedFilters, sessionToken, activeTab]);

    useEffect(() => { setCurrentPage(1); }, [startDate, endDate, appliedFilters, activeTab]);
    useEffect(() => { fetchReport(); }, [fetchReport]);

    // ── Apply / Reset Filters ──
    const handleApplyFilters = () => {
        setAppliedFilters({
            ...(filterExpenseName ? { expenseName: filterExpenseName } : {}),
            ...(filterInvoiceNo ? { invoiceNo: filterInvoiceNo } : {}),
            ...(filterStoreId ? { storeId: filterStoreId } : {}),
            ...(filterMinAmount ? { minAmount: filterMinAmount } : {}),
            ...(filterMaxAmount ? { maxAmount: filterMaxAmount } : {})
        });
    };

    const handleResetFilters = () => {
        setFilterExpenseName("");
        setFilterInvoiceNo("");
        setFilterStoreId("");
        setFilterMinAmount("");
        setFilterMaxAmount("");
        setAppliedFilters({});
    };

    // ── Search filter ──
    const filteredData = data.filter(row => {
        if (!tableSearch) return true;
        const q = tableSearch.toLowerCase();
        return (
            (row.expenseName || "").toLowerCase().includes(q) ||
            (row.description || "").toLowerCase().includes(q) ||
            (row.paymentMode || "").toLowerCase().includes(q) ||
            String(row.amount || "").includes(q)
        );
    });

    // ── Custom Select Styles ──
    const customSelectStyles = {
        control: (base, state) => ({
            ...base,
            height: "44px", minHeight: "44px",
            background: "#f8fafc",
            borderColor: state.isFocused ? "#3c0008" : "#e2e8f0",
            borderRadius: "12px", padding: "0 4px",
            fontSize: "14px", color: "#1e293b", boxShadow: "none",
            "&:hover": { borderColor: "#3c0008" }
        }),
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        menu: (base) => ({ ...base, zIndex: 9999, borderRadius: "12px" })
    };

    // ── Helpers ──
    const paymentBadgeClass = (mode) => {
        if (!mode) return "badge-others";
        const m = mode.toUpperCase();
        if (m === "CASH") return "badge-cash";
        if (m === "CARD") return "badge-card";
        if (m === "UPI") return "badge-upi";
        return "badge-others";
    };

    const renderPageNumbers = () => {
        const pages = [];
        const window = 2;
        let start = Math.max(1, currentPage - window);
        let end = Math.min(totalPages, currentPage + window);
        if (currentPage <= window + 1) end = Math.min(totalPages, 2 * window + 1);
        if (currentPage >= totalPages - window) start = Math.max(1, totalPages - 2 * window);

        for (let i = start; i <= end; i++) {
            pages.push(
                <button
                    key={i}
                    className={`page-btn ${i === currentPage ? "active" : ""}`}
                    onClick={() => setCurrentPage(i)}
                >{i}</button>
            );
        }
        return pages;
    };

    const startItem = (currentPage - 1) * rowsPerPage + 1;
    const endItem = Math.min(currentPage * rowsPerPage, totalItems);

    return (
        <div className="product-layout">
            <div className="product-content">
                <main className="product-main">
                    <div className="sales-report-container">
                        {/* ── Header ── */}
                        <div className="sales-report-header" style={{ borderBottom: "none", paddingBottom: 0, marginBottom: 10 }}>
                            <div className="sales-report-title-block">
                                <h1>Expense Report</h1>
                                <p>Track and analyze salon expenses and category details</p>
                            </div>
                            <div className="sales-report-actions">

                                <div className={`filter-item ${dateRange === "Custom" ? "date-range-custom-active" : ""}`}>
                                    <div className="date-range-picker-combined">

                                        <select
                                            className="filter-select date-range-select"
                                            value={dateRange}
                                            onChange={(e) => {
                                                const range = e.target.value;
                                                setDateRange(range);

                                                const {
                                                    startDate: s,
                                                    endDate: eD
                                                } = getDateRange(range, startDate, endDate);

                                                setStartDate(s);
                                                setEndDate(eD);
                                            }}
                                        >
                                            <option value="Today">Today</option>
                                            <option value="Yesterday">Yesterday</option>
                                            <option value="Last 7 Days">Last 7 Days</option>
                                            <option value="Last Month">Last Month</option>
                                            <option value="Current Month">Current Month</option>
                                            <option value="Current FY">Current Financial Year</option>
                                            <option value="Previous FY">Previous Financial Year</option>
                                            <option value="Custom">Custom Date</option>
                                        </select>

                                        {dateRange === "Custom" ? (
                                            <div className="custom-date-inputs">
                                                <input
                                                    type="date"
                                                    className="filter-input date-input"
                                                    value={startDate}
                                                    onChange={(e) => setStartDate(e.target.value)}
                                                />

                                                <span className="date-separator">
                                                    to
                                                </span>

                                                <input
                                                    type="date"
                                                    className="filter-input date-input"
                                                    value={endDate}
                                                    onChange={(e) => setEndDate(e.target.value)}
                                                />
                                            </div>
                                        ) : (
                                            <div className="date-display-box">
                                                <Icon
                                                    icon="mdi:calendar-outline"
                                                    width="18"
                                                />

                                                <span>
                                                    {startDate === endDate
                                                        ? startDate
                                                        : `${startDate} - ${endDate}`}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <button
                                    className="sales-action-btn primary"
                                    onClick={fetchReport}
                                >
                                    <Icon icon="mdi:refresh" width="16" />
                                    Refresh
                                </button>

                            </div>
                        </div>

                        {/* ── Tabs Section ── */}
                        <div className="sales-report-tabs-wrapper" style={{ marginBottom: 20 }}>
                            <div className="sales-report-tabs">
                                <button
                                    className={`sales-report-tab ${activeTab === "overall-expenses" ? "active" : ""}`}
                                    onClick={() => setActiveTab("overall-expenses")}
                                >
                                    Overall Expenses
                                </button>
                                <button
                                    className={`sales-report-tab ${activeTab === "category-wise" ? "active" : ""}`}
                                    onClick={() => setActiveTab("category-wise")}
                                >
                                    Category Wise
                                </button>
                            </div>
                        </div>

                        {/* ── Summary Cards ── */}
                        <div className="sales-summary-grid" style={{ gridTemplateColumns: "repeat(4, 1fr)" }}>
                            <div className="sales-summary-card card-total">
                                <div className="sales-summary-icon">
                                    <Icon icon="mdi:cash-multiple" width="26" />
                                </div>
                                <div className="sales-summary-info">
                                    <h3>Total Expenses</h3>
                                    <p className="sales-summary-value">
                                        {(summary.overallTotalAmount)}
                                        <span style={{ fontSize: "14px", color: "#94a3b8", fontWeight: 500, marginLeft: "8px" }}>({totalItems})</span>
                                    </p>
                                </div>
                            </div>
                            <div className="sales-summary-card card-avg">
                                <div className="sales-summary-icon">
                                    <Icon icon="mdi:calculator" width="26" />
                                </div>
                                <div className="sales-summary-info">
                                    <h3>Average Expense</h3>
                                    <p className="sales-summary-value">{(totalItems > 0 ? summary.overallTotalAmount / totalItems : 0)}</p>
                                </div>
                            </div>
                            <div className="sales-summary-card card-billable">
                                <div className="sales-summary-icon">
                                    <Icon icon="mdi:receipt-text-check" width="26" />
                                </div>
                                <div className="sales-summary-info">
                                    <h3>Billable Amount</h3>
                                    <p className="sales-summary-value">{(summary.totalBillableAmount)}</p>
                                </div>
                            </div>
                            <div className="sales-summary-card card-nonbillable">
                                <div className="sales-summary-icon">
                                    <Icon icon="mdi:receipt-text-remove" width="26" />
                                </div>
                                <div className="sales-summary-info">
                                    <h3>Non-Billable</h3>
                                    <p className="sales-summary-value">{(summary.totalNonBillableAmount)}</p>
                                </div>
                            </div>
                        </div>

                        {/* ── Filters ── */}
                        <div className="sales-filter-panel">
                            <div className="filter-grid" style={{ gridTemplateColumns: `repeat(${2 + (isSuperAdmin ? 1 : 0) + (activeTab === "overall-expenses" ? 1 : 0)}, 1fr)` }}>
                                <div className="filter-item">
                                    <label>Expense Category</label>
                                    <Select
                                        className="filter-select-container"
                                        classNamePrefix="filter-select"
                                        value={filterExpenseName ? { value: filterExpenseName, label: filterExpenseName } : null}
                                        onChange={selected => setFilterExpenseName(selected?.value || "")}
                                        options={EXPENSE_NAMES.map(n => ({ value: n, label: n }))}
                                        isClearable
                                        placeholder="All Categories"
                                        styles={{
                                            control: (base) => ({
                                                ...base,
                                                minHeight: '44px',
                                                borderRadius: '12px',
                                                border: '1px solid #e2e8f0',
                                                background: '#f8fafc',
                                                boxShadow: 'none',
                                                '&:hover': { borderColor: '#3c0008' }
                                            }),
                                            option: (base, state) => ({
                                                ...base,
                                                backgroundColor: state.isSelected ? '#3c0008' : state.isFocused ? '#f8fafc' : 'white',
                                                color: state.isSelected ? 'white' : '#1e293b',
                                                '&:active': { backgroundColor: '#3c0008' }
                                            })
                                        }}
                                    />
                                </div>
                                {isSuperAdmin && (
                                    <div className="filter-item">
                                        <label>Branch</label>
                                        <select
                                            className="filter-select"
                                            value={filterStoreId}
                                            onChange={e => setFilterStoreId(e.target.value)}
                                        >
                                            <option value="">All Branches</option>
                                            {storeList.map(s => (
                                                <option key={s._id} value={s._id}>{s.branchName || s.storeName}</option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                                {activeTab === "overall-expenses" && (
                                    <div className="filter-item">
                                        <label>Search Invoice/Ref #</label>
                                        <input
                                            type="text"
                                            className="filter-input"
                                            placeholder="Search #..."
                                            value={filterInvoiceNo}
                                            onChange={e => setFilterInvoiceNo(e.target.value)}
                                        />
                                    </div>
                                )}
                                <div className="filter-item">
                                    <label>Min / Max Amount</label>
                                    <div style={{ display: "flex", gap: "8px" }}>
                                        <input
                                            type="number"
                                            className="filter-input"
                                            placeholder="Min"
                                            value={filterMinAmount}
                                            onChange={e => setFilterMinAmount(e.target.value)}
                                        />
                                        <input
                                            type="number"
                                            className="filter-input"
                                            placeholder="Max"
                                            value={filterMaxAmount}
                                            onChange={e => setFilterMaxAmount(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px", marginTop: "20px", borderTop: "1px solid #f1f5f9", paddingTop: "20px" }}>
                                <button className="filter-reset-btn" onClick={handleResetFilters}>
                                    <Icon icon="mdi:filter-remove" width="16" style={{ marginRight: 6 }} />
                                    Reset
                                </button>

                            </div>
                        </div>

                        {/* ── Table Card ── */}
                        <div className="sales-table-card">
                            <div className="table-header-row">
                                <h2 className="table-title">
                                    Expense Entries
                                </h2>
                                <div className="table-controls">
                                    <div className="search-wrapper">
                                        <Icon icon="mdi:magnify" className="search-icon-inside" />
                                        <input
                                            type="text"
                                            className="table-search-input"
                                            placeholder="Search expenses…"
                                            value={tableSearch}
                                            onChange={e => setTableSearch(e.target.value)}
                                        />
                                    </div>
                                    <div className="column-toggler-wrapper" ref={columnTogglerRef}>
                                        <button
                                            className="column-toggle-btn"
                                            onClick={() => setShowColumnToggler(v => !v)}
                                        >
                                            <Icon icon="mdi:table-column" width="16" />
                                            Columns
                                            <Icon icon={showColumnToggler ? "mdi:chevron-up" : "mdi:chevron-down"} width="14" />
                                        </button>
                                        {showColumnToggler && (
                                            <div className="column-toggler-dropdown">
                                                {(TAB_COLUMNS[activeTab] || TAB_COLUMNS["overall-expenses"]).filter(c => c !== "action").map(col => (
                                                    <label key={col} className="column-toggle-item">
                                                        <input
                                                            type="checkbox"
                                                            checked={visibleColumns[col]}
                                                            onChange={() => setVisibleColumns(prev => ({ ...prev, [col]: !prev[col] }))}
                                                        />
                                                        {COLUMN_LABELS[col]}
                                                    </label>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Table */}
                            <div className="sales-table-wrapper">
                                {loading ? (
                                    <div className="sales-loading-state">
                                        <div className="loading-spinner" />
                                        <span>Loading expense report…</span>
                                    </div>
                                ) : filteredData.length === 0 ? (
                                    <div className="sales-empty-state">
                                        <div className="empty-icon">📋</div>
                                        <p>No expense records found for the selected period.</p>
                                    </div>
                                ) : (
                                    <table className="sales-data-table">
                                        <thead>
                                            <tr>
                                                <th style={{ width: 50 }}>#</th>
                                                {(TAB_COLUMNS[activeTab] || TAB_COLUMNS["overall-expenses"]).filter(c => visibleColumns[c]).map(col => (
                                                    <th key={col}>{COLUMN_LABELS[col]}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredData.map((row, idx) => {
                                                const currentColumns = (TAB_COLUMNS[activeTab] || TAB_COLUMNS["overall-expenses"]).filter(c => visibleColumns[c]);
                                                return (
                                                    <tr key={row._id || idx}>
                                                        <td style={{ color: "#94a3b8", fontWeight: 600 }}>
                                                            {(currentPage - 1) * rowsPerPage + idx + 1}
                                                        </td>
                                                        {currentColumns.includes("date") && (
                                                            <td>
                                                                <div style={{ display: "flex", flexDirection: "column" }}>
                                                                    <span style={{ fontWeight: 600, color: "#1e293b" }}>{formatDate(row.date || row.createdAt)}</span>
                                                                    {row.createdAt && <span style={{ fontSize: "11px", color: "#94a3b8" }}>{new Date(row.createdAt).toLocaleTimeString("en-IN", { hour: '2-digit', minute: '2-digit' })}</span>}
                                                                </div>
                                                            </td>
                                                        )}
                                                        {currentColumns.includes("expenseName") && (
                                                            <td>
                                                                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                                                    <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: row.expenseType === 'Billable' ? '#16a34a' : '#dc2626' }}></div>
                                                                    <strong style={{ color: "#334155" }}>{row.expenseName || row.otherExpenseName || "—"}</strong>
                                                                </div>
                                                            </td>
                                                        )}
                                                        {currentColumns.includes("count") && (
                                                            <td style={{ fontWeight: 600, color: "#475569" }}>
                                                                <span style={{ background: "#f1f5f9", padding: "4px 10px", borderRadius: "8px" }}>{row.count || 0}</span>
                                                            </td>
                                                        )}
                                                        {currentColumns.includes("description") && (
                                                            <td style={{ maxWidth: 220, whiteSpace: "normal", color: "#64748b", fontSize: "13px", lineHeight: "1.4" }}>
                                                                {row.description || "—"}
                                                            </td>
                                                        )}
                                                        {currentColumns.includes("amount") && (
                                                            <td style={{ color: "#3c0008", fontWeight: 800, fontSize: "15px" }}>
                                                                {(row.amount)}
                                                            </td>
                                                        )}
                                                        {currentColumns.includes("expenseType") && (
                                                            <td>
                                                                <span className={`status-badge ${row.expenseType === "Billable" ? "badge-billable" : "badge-nonbillable"}`}>
                                                                    {row.expenseType || "—"}
                                                                </span>
                                                            </td>
                                                        )}
                                                        {currentColumns.includes("paymentMode") && (
                                                            <td>
                                                                {row.paymentMode ? (
                                                                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                                                        <Icon
                                                                            icon={row.paymentMode.toUpperCase() === 'UPI' ? "mdi:cellphone-nfc" : row.paymentMode.toUpperCase() === 'CARD' ? "mdi:credit-card" : "mdi:cash"}
                                                                            style={{ color: "#64748b" }}
                                                                        />
                                                                        <span className={`status-badge ${paymentBadgeClass(row.paymentMode)}`} style={{ padding: "2px 8px" }}>
                                                                            {row.paymentMode}
                                                                        </span>
                                                                    </div>
                                                                ) : "—"}
                                                            </td>
                                                        )}
                                                        {currentColumns.includes("invoiceNo") && (
                                                            <td>
                                                                <span style={{ fontFamily: "monospace", background: "#f8fafc", padding: "3px 6px", borderRadius: "4px", border: "1px solid #e2e8f0", color: "#475569" }}>
                                                                    {row.invoiceNo || "—"}
                                                                </span>
                                                            </td>
                                                        )}
                                                        {currentColumns.includes("action") && (
                                                            <td>
                                                                <button
                                                                    className="action-view-btn"
                                                                    title="View Expense Details"
                                                                    onClick={() => setSelectedRecord(row)}
                                                                >
                                                                    <Icon icon="mdi:eye-outline" width="16" />
                                                                </button>
                                                            </td>
                                                        )}
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                )}
                            </div>

                            {/* Pagination */}
                            {!loading && totalItems > 0 && (
                                <div className="table-pagination-row">
                                    <span className="pagination-info">
                                        Showing {startItem}–{endItem} of {totalItems} entries
                                    </span>
                                    <div className="pagination-controls">
                                        <select
                                            className="page-size-select"
                                            value={rowsPerPage}
                                            onChange={e => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                                        >
                                            {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n} / page</option>)}
                                        </select>
                                        <button
                                            className="page-btn"
                                            onClick={() => setCurrentPage(1)}
                                            disabled={currentPage === 1}
                                        >
                                            <Icon icon="mdi:chevron-double-left" width="16" />
                                        </button>
                                        <button
                                            className="page-btn"
                                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                            disabled={currentPage === 1}
                                        >
                                            <Icon icon="mdi:chevron-left" width="16" />
                                        </button>
                                        {renderPageNumbers()}
                                        <button
                                            className="page-btn"
                                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                            disabled={currentPage === totalPages}
                                        >
                                            <Icon icon="mdi:chevron-right" width="16" />
                                        </button>
                                        <button
                                            className="page-btn"
                                            onClick={() => setCurrentPage(totalPages)}
                                            disabled={currentPage === totalPages}
                                        >
                                            <Icon icon="mdi:chevron-double-right" width="16" />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* ── View Modal ── */}
                        {selectedRecord && (
                            <div className="modal-overlay" onClick={() => setSelectedRecord(null)}>
                                <div className="expense-modal" onClick={e => e.stopPropagation()}>
                                    <div className="expense-modal-header">
                                        <div>
                                            <h2>Expense Details</h2>
                                            <p>{selectedRecord.expenseName || selectedRecord.otherExpenseName || "Expense Entry"}</p>
                                        </div>
                                        <button className="modal-close-btn" onClick={() => setSelectedRecord(null)}>
                                            <Icon icon="mdi:close" width="20" />
                                        </button>
                                    </div>
                                    <div className="expense-modal-body">
                                        {/* Type Banner */}
                                        <div className={`expense-type-banner ${selectedRecord.expenseType === "Billable" ? "billable" : "nonbillable"}`}>
                                            <Icon
                                                icon={selectedRecord.expenseType === "Billable" ? "mdi:receipt-text-check" : "mdi:receipt-text-remove"}
                                                width="20"
                                            />
                                            {selectedRecord.expenseType || "—"} Expense
                                        </div>

                                        {/* Details */}
                                        <div>
                                            <div className="modal-section-title">
                                                <Icon icon="mdi:information-outline" width="14" />
                                                Expense Information
                                            </div>
                                            <div className="modal-detail-list">
                                                <div className="modal-detail-row">
                                                    <span className="detail-label">Amount</span>
                                                    <span className="detail-value highlight">{(selectedRecord.amount)}</span>
                                                </div>
                                                {selectedRecord.date && (
                                                    <div className="modal-detail-row">
                                                        <span className="detail-label">Date</span>
                                                        <span className="detail-value">{formatDate(selectedRecord.date)}</span>
                                                    </div>
                                                )}
                                                {selectedRecord.expenseName && (
                                                    <div className="modal-detail-row">
                                                        <span className="detail-label">Category</span>
                                                        <span className="detail-value">{selectedRecord.expenseName}</span>
                                                    </div>
                                                )}
                                                {selectedRecord.otherExpenseName && (
                                                    <div className="modal-detail-row">
                                                        <span className="detail-label">Other Name</span>
                                                        <span className="detail-value">{selectedRecord.otherExpenseName}</span>
                                                    </div>
                                                )}
                                                {selectedRecord.description && (
                                                    <div className="modal-detail-row">
                                                        <span className="detail-label">Description</span>
                                                        <span className="detail-value" style={{ textAlign: "right", whiteSpace: "normal" }}>
                                                            {selectedRecord.description}
                                                        </span>
                                                    </div>
                                                )}
                                                {selectedRecord.paymentMode && (
                                                    <div className="modal-detail-row">
                                                        <span className="detail-label">Payment Mode</span>
                                                        <span className={`status-badge ${paymentBadgeClass(selectedRecord.paymentMode)}`}>
                                                            {selectedRecord.paymentMode}
                                                        </span>
                                                    </div>
                                                )}
                                                {selectedRecord.invoiceNo && (
                                                    <div className="modal-detail-row">
                                                        <span className="detail-label">Invoice No.</span>
                                                        <span className="detail-value">#{selectedRecord.invoiceNo}</span>
                                                    </div>
                                                )}
                                                {selectedRecord.createdAt && (
                                                    <div className="modal-detail-row">
                                                        <span className="detail-label">Recorded On</span>
                                                        <span className="detail-value">
                                                            {new Date(selectedRecord.createdAt).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </main>

            </div>
        </div>

    );
}
